import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { OptionallyVisible, PageWrapper } from 'components';
import { FormikHelpers } from 'formik';
import { useNotifications } from 'providers/notifications/useNotifications';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import merchantsApi from 'services/merchants';
import { CryptoPayoutResponse, MerchantInfo } from 'services/merchants/types';

import { ButtonWithLoading } from 'components/buttons/buttonWithLoading';
import { Panel } from 'components/containers';
import { FieldTitle, FormRow } from 'components/form/formLayout/styles';
import { RemoteSelect } from 'components/inputs/select/remoteSelect';
import { SelectItem } from 'components/inputs/select/select';

import { APIError, ValidationErrorMeta } from 'types/error';
import { NotificationSeverity } from 'types/notifications';

import { getValidationDetails } from 'utils/error';

import AgentDashboardContainer from '../agentDashboard/container';
import { ConfirmationModal } from './components/ConfirmationModal';
import { CUSTOMER_BASE_FIELDS, FORM_FIELDS, FormFieldType } from './form/keys';
import CustomerDetailsForm from './form/sections/customerDetails';
import TaxRefundForm from './form/sections/taxRefund';
import { useConfirmationModal } from './hooks/useConfirmationModal';
import { useCryptoPayoutForm } from './hooks/useCryptoPayoutForm';
import LABELS, { BREADCRUMBS } from './keys';
import { ButtonWrapper, Header, PanelContent, RemoteSelectWrapper } from './styles';

interface CryptoPayoutCreateProps {
  merchant?: MerchantInfo;
  residentialCountries: SelectItem[];
  canCreateOrdersForDifferentMerchants: boolean;
  readOnlyMode: boolean;
  getMerchants: (params: { search?: string; pagination?: { itemsPerPage: number; page: number } }) => Promise<{ data: SelectItem[] }>;
}

const CryptoPayoutCreate = ({
  merchant,
  residentialCountries,
  getMerchants,
  canCreateOrdersForDifferentMerchants,
  readOnlyMode,
}: CryptoPayoutCreateProps) => {
  const { pushToast } = useNotifications();
  const { t } = useTranslation();
  const breadcrumbs = BREADCRUMBS.map((breadcrumb) => t(breadcrumb));
  const [createdPayout, setCreatedPayout] = useState<CryptoPayoutResponse | undefined>();
  const [merchantUuid, setMerchantUuid] = useState<string | undefined>(merchant?.uuid);
  const isActionDisabled = !merchantUuid && canCreateOrdersForDifferentMerchants;

  const [createCryptoPayoutMutation, { isLoading, reset }] = merchantsApi.useCreateCryptoPayoutMutation();

  const getToastErrorMessage = (error?: { data: APIError }) => {
    if (error?.data.errorMessage) {
      return `${t(LABELS.ORDER_CREATION_ERROR)}: ${error.data.errorMessage}`;
    }

    return t(LABELS.ORDER_CREATION_ERROR);
  };
  const mapApiErrorsToFormFields = (apiErrors: Record<string, string> | undefined): Record<string, string> => {
    if (!apiErrors) return {};

    const errorMapping: Record<string, FormFieldType> = {
      email: FORM_FIELDS.EMAIL,
      firstName: FORM_FIELDS.FIRST_NAME,
      lastName: FORM_FIELDS.LAST_NAME,
      dob: FORM_FIELDS.DOB,
      countryOfResidence: FORM_FIELDS.COUNTRY_OF_RESIDENCE,
      fiatAmount: FORM_FIELDS.FIAT_AMOUNT,
      fiatCurrencyCode: FORM_FIELDS.CURRENCY,
      externalPayoutId: FORM_FIELDS.EXTERNAL_PAYOUT_ID,
    };

    const mappedErrors: Record<string, string> = {};
    Object.entries(apiErrors).forEach(([apiPath, message]) => {
      const formField = errorMapping[apiPath];
      if (formField) {
        mappedErrors[formField] = message;
      }
    });

    return mappedErrors;
  };

  const onSubmit = async (values: Record<FormFieldType, any>, { resetForm }: FormikHelpers<Record<FormFieldType, any>>) => {
    if (!merchantUuid) return;

    const customer = Object.fromEntries(CUSTOMER_BASE_FIELDS.map((field) => [field, values[field]]));

    const payload = {
      merchantUuid,
      customer,
      fiatAmount: parseFloat(values[FORM_FIELDS.FIAT_AMOUNT]),
      fiatCurrencyCode: values[FORM_FIELDS.CURRENCY].toLowerCase(),
      ...(values[FORM_FIELDS.EXTERNAL_PAYOUT_ID] && { externalPayoutId: values[FORM_FIELDS.EXTERNAL_PAYOUT_ID] }),
    };

    const result = await createCryptoPayoutMutation(payload);

    if ('error' in result) {
      pushToast({
        severity: NotificationSeverity.error,
        message: getToastErrorMessage(result.error as { data: APIError }),
      });

      const meta = (result.error as FetchBaseQueryError).data as { meta: ValidationErrorMeta };
      const apiErrors = meta?.meta ? getValidationDetails(meta.meta) : undefined;
      const formErrors = mapApiErrorsToFormFields(apiErrors);
      cryptoPayoutForm.setErrors(formErrors);
    } else {
      setCreatedPayout(result.data);
      resetForm();
    }
  };

  const cryptoPayoutForm = useCryptoPayoutForm({
    onSubmit,
  });

  const confirmationModal = useConfirmationModal({ form: cryptoPayoutForm });

  const onFieldChange = (field: FormFieldType, value: any) => {
    cryptoPayoutForm.setFieldValue(field, value);
    cryptoPayoutForm.setFieldError(field, undefined);
  };

  const onMerchantChange = (item: SelectItem, _?: string) => {
    setMerchantUuid(item.key);
    cryptoPayoutForm.setFieldValue(FORM_FIELDS.CURRENCY, (item as SelectItem & { value: MerchantInfo }).value.mainFiatCurrency);
  };

  const onStartOver = () => {
    reset();
    setCreatedPayout(undefined);
  };

  if (createdPayout && createdPayout.data) {
    return <AgentDashboardContainer uuid={createdPayout?.data?.uuid} token={createdPayout?.data?.token} onStartOver={onStartOver} />;
  }

  return (
    <PageWrapper breadcrumbs={breadcrumbs}>
      <Header>{t(LABELS.PAGE_TITLE)}</Header>
      <OptionallyVisible visible={canCreateOrdersForDifferentMerchants}>
        <Panel label={t(LABELS.MERCHANT_SELECTION)}>
          <PanelContent>
            <FormRow>
              <FieldTitle>{t(LABELS.MERCHANT_SELECT_LABEL)}</FieldTitle>
              <RemoteSelectWrapper>
                <RemoteSelect<SelectItem, false>
                  fullWidth
                  value={merchantUuid ?? ''}
                  getItems={getMerchants}
                  placeholder={t(LABELS.MERCHANT_SELECT_PLACEHOLDER)}
                  onChange={onMerchantChange}
                  getItemLabel={(item) => item.label}
                  closeDropdownOnSelect
                  leaveSearchValueOnClose
                />
              </RemoteSelectWrapper>
            </FormRow>
          </PanelContent>
        </Panel>
      </OptionallyVisible>
      <TaxRefundForm
        readOnlyMode={isLoading}
        values={cryptoPayoutForm.values}
        setFieldValue={onFieldChange}
        error={cryptoPayoutForm.errors}
      />
      <CustomerDetailsForm
        residentialCountries={residentialCountries}
        readOnlyMode={isLoading}
        errors={cryptoPayoutForm.errors}
        values={cryptoPayoutForm.values}
        setFieldValue={onFieldChange}
      />
      <OptionallyVisible visible={!readOnlyMode}>
        <ButtonWrapper>
          <ButtonWithLoading
            isLoading={isLoading}
            onClick={confirmationModal.openModal}
            primary
            disabled={readOnlyMode || isActionDisabled}
          >
            {t(LABELS.CONTROL_LABEL)}
          </ButtonWithLoading>
        </ButtonWrapper>
      </OptionallyVisible>
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        isLoading={isLoading}
        formValues={cryptoPayoutForm.values}
        onConfirm={confirmationModal.confirm}
        onCancel={confirmationModal.cancel}
      />
    </PageWrapper>
  );
};

export default CryptoPayoutCreate;
